import React, { createContext, useContext, useEffect, useState } from "react";
import companyService from "@/services/companyService";

interface AppContextType {
  companyDetails: any;
  loading: boolean;
}

const AppContext = createContext<AppContextType>({
  companyDetails: null,
  loading: true,
});

export const useAppContext = () => useContext(AppContext);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companyDetails, setCompanyDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const hasFetched = React.useRef(false);

  // Fetch company details by domain
  useEffect(() => {
    const fetchCompanyByDomain = async () => {
      try {
        const hostname = window.location.hostname;
        const parts = hostname.split(".");
        let subdomain = "";

        if (parts.length >= 2) {
          subdomain = parts[0];
        }

        if (subdomain && subdomain !== "www") {
          const response = await companyService.getByDomain(subdomain);
          if (response && response.company) {
            setCompanyDetails(response.company);
          }
        }
      } catch (error) {
        console.error("Failed to fetch company details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchCompanyByDomain();
  }, []);

  // Set favicon globally when company details change
  // Priority: 1. Favicon Resource 2. Logo Resource (Presigned) 3. Logo Resource (CloudFront)
  useEffect(() => {
    const faviconUrl =
      companyDetails?.faviconResource?.presignedUrl ||
      companyDetails?.logoResource?.presignedUrl ||
      (companyDetails?.logoResource?.s3Key
        ? `https://d3amt582gbk35t.cloudfront.net/${companyDetails.logoResource.s3Key}`
        : null);

      const finalFaviconUrl = faviconUrl || "/favicon.jpg"; // Fallback to default favicon
    
      const link = (document.querySelector("link[rel*='icon']") || document.createElement("link")) as HTMLLinkElement;
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      link.href = finalFaviconUrl;
      document.getElementsByTagName("head")[0].appendChild(link);
  }, [companyDetails]);

  return (
    <AppContext.Provider value={{ companyDetails, loading }}>
      {children}
    </AppContext.Provider>
  );
};